import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
    Image,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useUserApi } from '@/shared/services/userApi';

// Default avatar placeholder (inline SVG data URL)
// prettier-ignore
const DEFAULT_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"%3E%3Ccircle cx="60" cy="60" r="60" fill="%23e9ecef"/%3E%3Ccircle cx="60" cy="45" r="20" fill="%23adb5bd"/%3E%3Cellipse cx="60" cy="95" rx="35" ry="25" fill="%23adb5bd"/%3E%3C/svg%3E'; // eslint-disable-line max-len

export default function CreateProfile() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [bio, setBio] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Profile picture state
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [pictureError, setPictureError] = useState('');

    const { user, refreshProfileStatus } = useAuth();
    const userApi = useUserApi();
    const navigate = useNavigate();

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
        ];
        if (!allowedTypes.includes(file.type)) {
            setPictureError('Please select a JPEG, PNG, or GIF image.');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setPictureError('File size must be less than 5MB.');
            return;
        }

        // Clear errors and set file
        setPictureError('');
        setProfilePicture(file);
        setProfilePicturePreview(URL.createObjectURL(file));
    };

    const handleRemovePicture = () => {
        setProfilePicture(null);
        setProfilePicturePreview(null);
        setPictureError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!acceptedTerms || !acceptedPrivacy) {
            setError(
                'You must accept the Terms & Conditions and Privacy Policy to continue.'
            );
            return;
        }

        setLoading(true);

        try {
            // Create the profile first
            await userApi.createProfile({
                email: user.email,
                name: {
                    first: firstName,
                    last: lastName,
                },
                bio,
                termsAndConditions: {
                    accepted: true,
                    dateAccepted: new Date(),
                    versionNumber: '1.0',
                },
                privacyPolicy: {
                    accepted: true,
                    dateAccepted: new Date(),
                    versionNumber: '1.0',
                },
            });

            // Upload profile picture if one was selected
            if (profilePicture) {
                try {
                    const formData = new FormData();
                    formData.append('profilePicture', profilePicture);
                    await userApi.uploadProfilePicture(formData);
                } catch (uploadError) {
                    // Log error but don't fail the whole process
                    console.error(
                        'Profile picture upload failed:',
                        uploadError
                    );
                }
            }

            refreshProfileStatus();
            // Hand off to the ecosystem chooser. After signup-create-profile
            // the 0-membership case is most common (the user has not joined
            // any ecosystem yet); the chooser surfaces the "browse
            // ecosystems" welcome path. If the user already had a membership
            // (rare for create-profile but possible, e.g. a returning user
            // recreating their profile), the chooser auto-routes them to it.
            navigate('/choose-ecosystem');
        } catch (err) {
            let errorMessage = 'Failed to create profile. Please try again.';

            if (err.response?.status === 409) {
                errorMessage =
                    'A profile already exists for this account. Please use the Edit Profile feature instead.';
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            setError(errorMessage);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Create Profile | RILE Connect</title>
            </Helmet>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <Card className="shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="text-center text-primary mb-2">
                                    Complete Your Profile
                                </h2>

                                {user?.email && (
                                    <p className="text-center text-muted small mb-4">
                                        Signed in as: {user.email}
                                    </p>
                                )}

                                <p className="text-center text-muted mb-4">
                                    Tell us a bit about yourself to get started
                                    with RILE Connect.
                                </p>

                                {error && (
                                    <Alert variant="danger">{error}</Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    {/* Profile Picture Section */}
                                    <Form.Group className="mb-4 text-center">
                                        <Form.Label className="d-block fw-semibold mb-2">
                                            Profile Picture{' '}
                                            <span className="text-muted fw-normal">
                                                (optional)
                                            </span>
                                        </Form.Label>

                                        <div className="mb-3">
                                            <Image
                                                src={
                                                    profilePicturePreview ||
                                                    DEFAULT_AVATAR
                                                }
                                                alt="Profile preview"
                                                roundedCircle
                                                className="profile-avatar-preview"
                                            />
                                        </div>

                                        <div className="d-flex justify-content-center gap-2">
                                            <Form.Control
                                                type="file"
                                                accept=".jpg,.jpeg,.png,.gif"
                                                onChange={handleFileChange}
                                                disabled={loading}
                                                className="d-none"
                                                id="profile-picture-input"
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() =>
                                                    document
                                                        .getElementById(
                                                            'profile-picture-input'
                                                        )
                                                        .click()
                                                }
                                                disabled={loading}
                                            >
                                                {profilePicture
                                                    ? 'Change Photo'
                                                    : 'Choose Photo'}
                                            </Button>
                                            {profilePicture && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={
                                                        handleRemovePicture
                                                    }
                                                    disabled={loading}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>

                                        {pictureError && (
                                            <div className="text-danger small mt-2">
                                                {pictureError}
                                            </div>
                                        )}

                                        <Form.Text className="text-muted d-block mt-1">
                                            JPEG, PNG, or GIF (max 5MB)
                                        </Form.Text>
                                    </Form.Group>

                                    <hr className="my-4" />

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    First Name *
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) =>
                                                        setFirstName(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="First name"
                                                    required
                                                    disabled={loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Last Name *
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) =>
                                                        setLastName(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Last name"
                                                    required
                                                    disabled={loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Bio{' '}
                                            <span className="text-muted fw-normal">
                                                (optional)
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={bio}
                                            onChange={(e) =>
                                                setBio(e.target.value)
                                            }
                                            placeholder="Tell us about yourself..."
                                            disabled={loading}
                                        />
                                    </Form.Group>

                                    <hr className="my-4" />

                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="terms"
                                            label={
                                                <span>
                                                    I accept the{' '}
                                                    <a
                                                        href="/policies/terms"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms & Conditions
                                                    </a>
                                                </span>
                                            }
                                            checked={acceptedTerms}
                                            onChange={(e) =>
                                                setAcceptedTerms(
                                                    e.target.checked
                                                )
                                            }
                                            required
                                            disabled={loading}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Check
                                            type="checkbox"
                                            id="privacy"
                                            label={
                                                <span>
                                                    I accept the{' '}
                                                    <a
                                                        href="/policies/privacy"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Privacy Policy
                                                    </a>
                                                </span>
                                            }
                                            checked={acceptedPrivacy}
                                            onChange={(e) =>
                                                setAcceptedPrivacy(
                                                    e.target.checked
                                                )
                                            }
                                            required
                                            disabled={loading}
                                        />
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100 rounded-pill"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Creating Profile...'
                                            : 'Create Profile'}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
